import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Search, CheckCircle2, XCircle, AlertCircle, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAppConfig } from "../lib/AppConfigContext";
import api from "../lib/api";
import { LegalFooter } from "../components/LegalFooter";

export default function CensusCheckPage() {
  const { t, i18n } = useTranslation();
  const { consultaId } = useParams();
  const { appName } = useAppConfig();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disabled, setDisabled] = useState(false);

  const [dni, setDni] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [blocked, setBlocked] = useState(false);

  const [showPasswordCheck, setShowPasswordCheck] = useState(false);
  const [password, setPassword] = useState("");
  const [checkingPassword, setCheckingPassword] = useState(false);
  const [passwordResult, setPasswordResult] = useState(null);

  useEffect(() => {
    loadInfo();
  }, [consultaId]);

  const loadInfo = async () => {
    try {
      const res = await api.get(`/public/census-check-info/${consultaId}`);
      setInfo(res.data);
      if (res.data.language && res.data.language !== i18n.language) {
        i18n.changeLanguage(res.data.language);
      }
      if (res.data.voting_started) setDisabled(true);
    } catch {
      setError(t('errors.invalidLink'));
    } finally {
      setLoading(false);
    }
  };

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!dni.trim() || !birthDate.trim()) {
      toast.error(t('errors.allFieldsRequired'));
      return;
    }
    setChecking(true);
    setResult(null);
    setShowPasswordCheck(false);
    setPasswordResult(null);
    try {
      const res = await api.post("/public/census-check", {
        consulta_id: consultaId,
        dni: dni.trim(),
        birth_date: birthDate.trim(),
      });
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setBlocked(true);
      }
      toast.error(err.response?.data?.detail || t('censusCheck.checkError'));
    } finally {
      setChecking(false);
    }
  };

  const handlePasswordCheck = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error(t('censusCheck.enterPasswordError'));
      return;
    }
    setCheckingPassword(true);
    setPasswordResult(null);
    try {
      const res = await api.post("/public/census-check-password", {
        consulta_id: consultaId,
        dni: dni.trim(),
        birth_date: birthDate.trim(),
        password,
      });
      setPasswordResult(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setBlocked(true);
        setShowPasswordCheck(false);
        setResult(null);
      }
      toast.error(err.response?.data?.detail || t('censusCheck.verifyError'));
    } finally {
      setCheckingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-900">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-6 text-center">
          {info?.entity_logo && (
            <img src={info.entity_logo} alt={info.entity_name} className="h-16 w-auto mx-auto object-contain" />
          )}
          <Card>
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-slate-900 mb-2">{t('censusCheck.notAvailable')}</h2>
              <p className="text-slate-600">
                {t('censusCheck.votingStarted', { title: info?.consulta_title })}
              </p>
            </CardContent>
          </Card>
          <p className="text-xs text-slate-400">{appName} - {t('censusCheck.secureVotingSystem')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="census-check-page">
      <div className="max-w-md w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          {info?.entity_logo && (
            <img src={info.entity_logo} alt={info.entity_name} className="h-16 w-auto mx-auto mb-3 object-contain" />
          )}
          <h1 className="text-2xl font-bold text-slate-900">{appName}</h1>
          <p className="text-slate-500 mt-1">{t('censusCheck.title')}</p>
          <p className="text-sm text-slate-400 mt-1">{info?.entity_name} - {info?.consulta_title}</p>
        </div>

        {/* Check Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Search className="h-5 w-5 text-blue-600" />
              {t('censusCheck.checkIfInCensus')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCheck} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="dni">{t('censusCheck.dniLabel')}</Label>
                <Input
                  id="dni"
                  data-testid="census-check-dni"
                  placeholder="12345678A"
                  value={dni}
                  onChange={(e) => setDni(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="birthDate">{t('censusCheck.birthDateLabel')}</Label>
                <Input
                  id="birthDate"
                  data-testid="census-check-birthdate"
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                disabled={checking}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="census-check-btn"
              >
                {checking ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    {t('common.checking')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    {t('censusCheck.check')}
                  </span>
                )}
              </Button>
            </form>

            {/* Results */}
            {blocked && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-800">{t('censusCheck.dniBlocked')}</p>
                  <p className="text-sm text-red-700 mt-1">{t('censusCheck.blockedDesc')}</p>
                </div>
              </div>
            )}

            {result && !blocked && (
              <div className="mt-4 space-y-3">
                {result.in_census ? (
                  <>
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3" data-testid="census-check-found">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-green-800">{t('censusCheck.inCensus')}</p>
                        {result.is_registered && (
                          <p className="text-sm text-green-700 mt-1">{t('censusCheck.alsoRegistered')}</p>
                        )}
                      </div>
                    </div>

                    {result.is_registered && !showPasswordCheck && (
                      <Button
                        variant="outline"
                        className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
                        onClick={() => setShowPasswordCheck(true)}
                        data-testid="verify-password-btn"
                      >
                        <KeyRound className="h-4 w-4 mr-2" />
                        {t('censusCheck.checkMyPassword')}
                      </Button>
                    )}

                    {showPasswordCheck && (
                      <Card className="border-blue-200">
                        <CardContent className="pt-4">
                          <form onSubmit={handlePasswordCheck} className="space-y-3">
                            <div className="space-y-2">
                              <Label htmlFor="password">{t('censusCheck.passwordLabel')}</Label>
                              <Input
                                id="password"
                                data-testid="census-check-password"
                                type="password"
                                placeholder={t('censusCheck.passwordPlaceholder')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                              />
                            </div>
                            <Button
                              type="submit"
                              disabled={checkingPassword}
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              data-testid="census-check-password-btn"
                            >
                              {checkingPassword ? t('common.verifying') : t('censusCheck.verifyPassword')}
                            </Button>
                          </form>

                          {passwordResult && (
                            <div className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${
                              passwordResult.password_correct
                                ? "bg-green-50 border border-green-200"
                                : "bg-red-50 border border-red-200"
                            }`} data-testid="password-result">
                              {passwordResult.password_correct ? (
                                <>
                                  <ShieldCheck className="h-5 w-5 text-green-600" />
                                  <p className="text-sm font-medium text-green-800">{t('censusCheck.passwordCorrect')}</p>
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-5 w-5 text-red-600" />
                                  <p className="text-sm font-medium text-red-800">
                                    {t('censusCheck.passwordIncorrect')}
                                    {passwordResult.remaining != null && `. ${t('censusCheck.remainingAttempts', { count: passwordResult.remaining })}`}
                                  </p>
                                </>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </>
                ) : (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="census-check-not-found">
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-red-800">{t('censusCheck.notInCensus')}</p>
                      <p className="text-sm text-red-700 mt-1">
                        {t('censusCheck.contactEntity')}
                        {result.remaining != null && ` ${t('censusCheck.remainingAttempts', { count: result.remaining })}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400">{appName} - {t('common.secureVotingSystem')}</p>
        <LegalFooter />
      </div>
    </div>
  );
}
